import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { page, track, packAndTrack } from '@v2/services/analytics.service';
import QuantityInput from '@components/common/helpers/quantity-input';
import { PackQuantityInput } from '@components/common/products/quantity';
import { useAddCartModal, useBuildBundleModal } from '@components/common/presentational/modal/common';
import { updateActiveBoxLine } from '@app/store/rtk.cart.store';

import { ContentLoader } from '@components/common/presentational/loader/loader';
import ProductService from '@services/product.service';
import { IProduct } from '@interfaces';
import { categoryMapSingle, useAddPackAction, useIsSwapping } from '@components/common/products/utils';

import ProductDescription from '@components/common/products/partial/product-description';
import ProductNutrients from '@components/common/products/partial/product-nutrients';
import ProductDevelopmental from '@components/common/products/partial/product-developmental';
import ProductIngredients from '@components/common/products/partial/product-ingredients';
import ProductStandard from '@components/common/products/partial/product-standard';
import ProductSafety from '@v2/components/common/products/partial/product-safety';
import ProductFDA from '@components/common/products/partial/product-fda';
import ProductMeta from '@components/common/products/partial/product-meta';

import styles from '@components/common/products/product.styles';
import { useGTMItemDispatch } from '@v2/utils/gtm';
import { WPImage } from '@v2/utils/image';
import Collaboration from './collaboration';
import { useEntity } from '@app/store/rtk.entities.selectors';
import { useActiveBoxIndex, useActiveBoxLine, useCartAllBoxes, useIsActiveBoxFull, useIsCategoryFull, usePackBoxIndex } from '@app/store/cart.selectors';
import Discontinuation from './discontinuation';
import ProductsRating from '@components/common/stamped/presentational/products-rating';
import { BadgesList } from '../presentational/badge/badges-list';
import ProductRatings from './partial/product-ratings';

function ProductAction({ product, close, isMobile, isNewBox, parentRef }
    : { product: IProduct, close: Function, isMobile?: boolean, isNewBox?: boolean, parentRef?: any }
) {
    const dispatch = useDispatch();
    const addPackAction = useAddPackAction();
    const [isScrolled, setIsScrolled] = useState(false);

    const blueprints = useEntity('blueprints');
    const allBoxes = useCartAllBoxes();
    const productId = product.id;

    const line = useActiveBoxLine(productId);
    const packBoxIndex = usePackBoxIndex(productId);
    const activeBoxIndex = useActiveBoxIndex();
    const isMaxed = useIsActiveBoxFull();
    const isCategoryMaxed = useIsCategoryFull(product);
    const count = line && line.count ? line.count : 0;
    const isPack = ['pouchpacks', 'smoothiepacks', 'barpacks', 'bonebrothpouchpacks'].includes(product.category);
    const [showBuilder] = useBuildBundleModal();
    const isSwapping = useIsSwapping();
    const [showCartModal] = useAddCartModal(packBoxIndex);

    const setCount = (count: number) => {
        dispatch(updateActiveBoxLine({ product, count }));
    }

    useEffect(() => {
        if (parentRef?.current && isMobile) {
            const onScroll = () => {
                const divposition = document.getElementById('top-container').getBoundingClientRect();
                const currentScrolled = divposition.top + divposition.height < 0;

                setIsScrolled(currentScrolled);
            };

            const element = document.getElementsByClassName('product-wrapper')[0].parentElement;

            if (element) {
                // clean up code
                element.removeEventListener('scroll', onScroll);
                element.addEventListener('scroll', onScroll, { passive: true });

                return () => element.removeEventListener('scroll', onScroll);
            }
        }
    }, [parentRef]);

    return (
        <div
            className={`${isScrolled ? 'scrolled' : ''} ${isMobile ? styles.productBar : styles.action}`}>
            {isMobile
                ?
                <div className="bar-product-image">
                    <WPImage
                    src={product.thumbnailImage}
                    alt={product.thumbnailImageAlt || product.title}
                    />
                    {product?.status === 'instock' && <Discontinuation productId={product.id} />}
                </div>
                :
                <div className="product-image-wrapper">
                    <WPImage
                    className={`product-image ${product.category}`}
                    src={product.thumbnailImage}
                    alt={product.thumbnailImageAlt || product.title}
                    />
                    {product?.status === 'instock' && <Discontinuation productId={product.id} />}
                </div>
            }
            <div className='ratings-section'>
                <div className="box-ingredients">
                    <BadgesList productTitle={product.title} />
                </div>
                <div className='products-rating-container'>
                    <ProductsRating />
                </div>
            </div>
            <div className="action-wrapper">
                {!isMobile &&
                    <>
                        <div className="prices">per {categoryMapSingle(product.category)}</div>
                        <div className="unit-price">
                            <div className="regular">
                                ${product.price.toFixed(2)}
                            </div>
                            <div className="discount">
                                ${product.priceSubscribed.toFixed(2)}
                            </div>
                        </div>
                        <div className="subscribe">Subscribe and Save 15%</div>
                    </>
                }
                {product?.status === 'instock' &&
                    <div className={isMobile ? 'bar-control' : 'actions'}>
                        {isPack
                            ?
                            <>
                                {allBoxes[packBoxIndex]
                                    ?
                                    <PackQuantityInput box={allBoxes[packBoxIndex]} />
                                    :
                                    <div className={`action add`}>
                                        <button
                                            className="button primary red"
                                            onClick={() => {
                                                addPackAction(packBoxIndex, product, blueprints, allBoxes);
                                                if(!isSwapping) {
                                                    showCartModal();
                                                }
                                            }}
                                            aria-label={'Add to bag'}>
                                           {isSwapping ? 'Select' : 'Add to bag'}
                                        </button>
                                    </div>
                                }
                            </>
                            :
                            <>
                                {activeBoxIndex !== null && allBoxes[activeBoxIndex] // && !isNewBox
                                    ?
                                    <>
                                        {!allBoxes[activeBoxIndex].lines[product.id]
                                            ?
                                            <div className={`action add`}>
                                                <button
                                                    disabled={isMaxed === null ? isMaxed : (isMaxed || isCategoryMaxed)}
                                                    className="button primary red"
                                                    onClick={() => setCount(1)}
                                                    aria-label={'Add to bundle'}>
                                                    Add to bundle
                                                </button>
                                            </div>
                                            :
                                            <QuantityInput
                                                count={count}
                                                setCount={setCount}
                                                title={product.title}
                                                isOutOfStock={false}
                                                isMaxed={isMaxed === null ? isMaxed : (isMaxed || isCategoryMaxed)}
                                                maxedType={(isCategoryMaxed && !isMaxed) ? product.category : null}
                                                isButtonText={true}
                                            />
                                        }
                                    </>
                                    :
                                    <div className={`action add`}>
                                        <button
                                            className="button primary red"
                                            onClick={() => {
                                                close();
                                                showBuilder();
                                                packAndTrack(
                                                    'Build Box Clicked',
                                                    [{ 'view': 'PLP', 'section': 'PLP' }]
                                                );
                                            }}
                                            aria-label={'Build a bundle'}>
                                            Build a bundle
                                        </button>
                                    </div>
                                }
                            </>
                        }

                    </div>
                }
                {!isMobile &&
                    <div className="text-center tm-text mt-3">
                        Smart food for a developing brain and body ™
                    </div>
                }
            </div>
        </div>
    );
}

function ProductNoAction({ product, isMobile }: { product: IProduct, isMobile?: boolean }) {

    return (
        <div className={isMobile ? styles.productBar : styles.action}>
            {isMobile
                ?
                <div className="bar-product-image">
                    <WPImage
                    src={product.thumbnailImage}
                    alt={product.thumbnailImageAlt || product.title}
                    />
                    {product?.status === 'instock' && <Discontinuation productId={product.id} />}
                </div>
                :
                <div className="product-image-wrapper">
                    <WPImage
                    className={`product-image ${product.category}`}
                    src={product.thumbnailImage}
                    alt={product.thumbnailImageAlt || product.title}
                    />
                    {product?.status === 'instock' && <Discontinuation productId={product.id} />}
                </div>
            }
            <div className='ratings-section'>
                <div className="box-ingredients">
                    <BadgesList productTitle={product.title} />
                </div>
                <div className='products-rating-container'>
                    <ProductsRating />
                </div>
            </div>
            <div className="action-wrapper">
                {!isMobile &&
                    <>
                        <div className="prices">per {categoryMapSingle(product.category)}</div>
                        <div className="unit-price">
                            <div className="regular">
                                ${product.price.toFixed(2)}
                            </div>
                            <div className="discount">
                                ${product.priceSubscribed.toFixed(2)}
                            </div>
                        </div>
                        <div className="subscribe">Subscribe and Save 15%</div>
                    </>
                }
            </div>
        </div>
    );
}

function ProductModalBody({ product, close, disabled, playVideo, isNewBox }:
    { product: IProduct, close: Function, disabled: boolean, playVideo?: boolean, isNewBox?: boolean }
) {
    const ref = useRef(null);

    return (
        <div className="product-wrapper" id="pdp" ref={ref}>
            {/* Commented to fix Story-5651, modal closing should be on cross button */}
            {/* <span onClick={()=>close()} style={{position: 'absolute', left: 0, top: 0, width: '100%', height: '100%'}}></span> */}
            <ProductMeta product={product} />
            <div className="left-side">
                <section className="animate__animated animate__zoomOutToIn" id="top-container">
                    {disabled
                        ?
                        <ProductNoAction product={product} />
                        :
                        <ProductAction product={product} close={close} isNewBox={isNewBox} />
                    }
                </section>
            </div>

            <div className="right-side">
                <section>
                    <ProductDescription product={product} playVideo={playVideo} />
                </section>
                <section>
                    <ProductFDA product={product} />
                </section>
                <section>
                    <ProductIngredients product={product} />
                </section>
                <section>
                    <ProductNutrients product={product} />
                </section>
                <section className="developmental-section">
                    <ProductDevelopmental product={product} />
                </section>
                <section className="standard-section">
                    <ProductStandard product={product} />
                </section>
                <section className="safety-usage-section">
                    <ProductSafety product={product} />
                </section>
                <section>
                    <ProductRatings />
                </section>
                {disabled
                    ?
                    <ProductNoAction product={product} isMobile={true} />
                    :
                    <ProductAction
                        product={product}
                        close={close}
                        isMobile={true}
                        isNewBox={isNewBox}
                        parentRef={ref} />
                }
                <div style={{ height: 50 }}></div>
            </div>
        </div>
    );
}

export default function ProductModal({ id, product, disabled, playVideo, isNewBox, hide }
    : { id?: number, product?: IProduct, disabled?: boolean, playVideo?: boolean, isNewBox?: boolean, hide?: Function }
) {
    const location = useLocation();
    const gtmDispatch = useGTMItemDispatch();

    const [loadedProduct, setLoadedProduct] = useState(null);

    function trackEvents(product: IProduct) {
        // https://segment.com/docs/connections/destinations/catalog/facebook-pixel/#standard-events
        // https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking/#object-properites
        // https://www.google.com/basepages/producttype/taxonomy-with-ids.en-US.txt

        page('Product', {
            url: window.location.href,
            path: window.location.pathname,
            cbReferrer: (location && location.state) ? (location.state as any).referrer : document.referrer,
            content_category: product.category,
            content_ids: [product.sku],
            content_name: product.title,
            content_type: 'product',
        });

        // This one is used by FB
        track('PDP Viewed', {
            id: product.sku,
            content_category: product.category,
            content_ids: [product.sku],
            content_name: product.title,
            content_type: 'product',
            product_availability: product?.status === 'instock' ? 'in stock' : 'out of stock',
            availability: product?.status === 'instock' ? 'in stock' : 'out of stock',
            title: product.title,
            link: `https://cerebelly.com/product/${product.category}/${product.slug}`,
            image_link: product.thumbnailImage ? `https://cerebelly.com/wp-content/uploads/${product.thumbnailImage}` : '',
            price: `${product.priceSubscribed} USD`,
            google_product_category: 5719,
            offers: {
                price: product.priceSubscribed,
                priceCurrency: 'USD',
                availability: product?.status === 'instock' ? 'in stock' : 'out of stock',
            }
        });

        const value = typeof product.priceSubscribed === 'number' ? product.priceSubscribed : parseFloat(product.priceSubscribed);
        gtmDispatch('view_item', product, { value: parseFloat(value.toFixed(2)) });
    }

    useEffect(() => {
        if (product) {
            trackEvents(product);
            setLoadedProduct(product);
        } else {
            ProductService
                .get(id)
                .then((product) => {
                    trackEvents(product);
                    setLoadedProduct(product);
                })
                .catch(console.error);
        }
    }, []);

    return (
        <div className={styles.overlay}
            tabIndex={-1}
            role="dialog">
            <ContentLoader loading={!loadedProduct}>
                {loadedProduct &&
                <>
                {loadedProduct.category === 'collaborations'
                    ?
                    <Collaboration
                        product={loadedProduct}
                        disabled={disabled}
                        playVideo={playVideo}
                        close={hide}
                        isNewBox={false} />
                    :
                    <ProductModalBody
                        product={loadedProduct}
                        disabled={disabled}
                        playVideo={playVideo}
                        close={hide}
                        isNewBox={false} />
                }
                </>
                }
            </ContentLoader>
        </div>
    );
}